import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, Loader2 } from 'lucide-react';
import { authAPI, hasSessionToken, refreshAuth } from '../utils/api';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { openInAppBrowser } from '../utils/capacitor';
import { PRIVACY_URL, TERMS_URL } from '../config/legalUrls';
import { backendOrigin } from '../config/backend';
import useUserStore from '../store/userStore';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser, setProfile } = useUserStore();
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState('');
  const [sendingMagicLink, setSendingMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [appleSignInLoading, setAppleSignInLoading] = useState(false);
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  // Explicit "show login UI" (account switch from MacroSetupChoice's Back):
  // never auto-route back into the persisted account.
  const switchRequested = searchParams.get('switch') === '1';
  // `checkingSession` = an ASYNC session recovery is in flight: a token exists
  // but no user is hydrated, so /auth/me must answer before we can route.
  // Already-known states never wait: a hydrated user routes during render
  // (below), and no token means the login buttons show immediately.
  const [checkingSession, setCheckingSession] = useState(
    () => !switchRequested && !useUserStore.getState().user && hasSessionToken()
  );
  // True when the axios 401 interceptor cleared this session moments ago
  // (raised via sessionStorage instead of a URL query param so the iOS
  // WKWebView never has to re-load index.html — the previous URL-based
  // approach caused white-screens on TestFlight when multiple concurrent
  // 401s issued overlapping `window.location.replace` calls).
  const [sessionExpiredBanner, setSessionExpiredBanner] = useState(false);

  useEffect(() => {
    setIsNativePlatform(Capacitor.isNativePlatform());
    try {
      if (sessionStorage.getItem('fitxess_session_expired') === '1') {
        setSessionExpiredBanner(true);
        sessionStorage.removeItem('fitxess_session_expired');
      }
    } catch (_e) { /* swallow */ }
  }, []);

  // Auto-restore session on mount. Covers the "app always shows login on
  // relaunch" bug on iOS: zustand persists the user, and our JWT sits in
  // localStorage, so we should skip the login screen whenever we can.
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      // Explicit "show login UI" signal from MacroSetupChoice's Back button.
      // The user wants to switch accounts or just see the login screen, so
      // skip auto-restore even though zustand still holds an authenticated
      // user. Cold launches (no switch param) keep the original behaviour.
      if (searchParams.get('switch') === '1') {
        if (!cancelled) setCheckingSession(false);
        return;
      }
      // Case A: zustand already has the user (persisted hydrate) → the render
      // below has already routed via <Navigate>; nothing async to do.
      if (user) return;
      // Case B: we still have a token but no user object — fetch /auth/me.
      // Storing the user re-renders into the same <Navigate> routing.
      if (hasSessionToken()) {
        try {
          const { user: fetched, profile } = await refreshAuth();
          if (cancelled) return;
          if (profile) setProfile(profile);
          setUser(fetched);
          return;
        } catch (e) {
          console.log('[Login] auto-restore failed, showing login screen', e?.message);
        }
      }
      // Case C: no session → render login buttons.
      if (!cancelled) setCheckingSession(false);
    };

    restore();
    return () => { cancelled = true; };
  }, [user, setUser, setProfile, searchParams]);

  const handleGoogleLogin = async () => {
    const isNative = Capacitor.isNativePlatform();
    const baseUrl = backendOrigin();
    const platform = isNative ? Capacitor.getPlatform() : 'web';
    const loginUrl = `${baseUrl}/api/auth/google/login?platform=${platform}`;
    console.log('[AUTH][GOOGLE] starting', { isNative, baseUrl, platform, loginUrl });
    if (isNative) {
      // Apple Guideline 4: present the OAuth consent flow in an in-app
      // SFSafariViewController rather than bouncing to the external Safari
      // app. The backend redirects back to our fitxess:// deep link, which
      // dismisses this sheet (see Browser.close() in capacitor.js).
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: loginUrl });
    } else {
      window.location.href = loginUrl;
    }
  };

  // Native Apple Sign-In for iOS
  const handleAppleLogin = async () => {
    console.log('[AUTH][APPLE] tapped. platform=', Capacitor.getPlatform(), 'isNative=', Capacitor.isNativePlatform());
    if (Capacitor.getPlatform() === 'ios') {
      setAppleSignInLoading(true);
      try {
        const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
        console.log('[AUTH][APPLE] plugin imported. Calling authorize…');
        
        const nonce = Math.random().toString(36).substring(2) + Date.now().toString(36);
        let result;
        try {
          result = await SignInWithApple.authorize({
            scopes: 'email name',
            nonce,
          });
          console.log('[AUTH][APPLE] authorize returned. has identityToken?', !!(result?.response?.identityToken));
        } catch (authErr) {
          console.error('[AUTH][APPLE] authorize() threw:', {
            message: authErr?.message,
            code: authErr?.code,
            errorMessage: authErr?.errorMessage,
            raw: JSON.stringify(authErr),
          });
          throw authErr;
        }

        if (result.response && result.response.identityToken) {
          const given = result.response.givenName || '';
          const family = result.response.familyName || '';
          const hasName = !!(given || family);

          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          console.log('[AUTH][APPLE] posting identity token to', `${backendUrl}/api/auth/apple`);
          const response = await fetch(`${backendUrl}/api/auth/apple`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              identity_token: result.response.identityToken,
              authorization_code: result.response.authorizationCode,
              nonce,
              user_data: hasName ? {
                name: { givenName: given, familyName: family }
              } : null,
            }),
          });
          console.log('[AUTH][APPLE] backend response status=', response.status);

          if (response.ok) {
            const data = await response.json();
            localStorage.setItem('fitxess_session_token', data.session_token);
            if (data.user) {
              localStorage.setItem('fitxess_user', JSON.stringify(data.user));
            }
            toast.success('Signed in with Apple!');
            navigate('/auth-callback?session_id=' + encodeURIComponent(data.session_token));
          } else {
            const error = await response.json().catch(() => ({}));
            console.error('[AUTH][APPLE] backend rejected:', error);
            toast.error(error.detail || 'Apple Sign-In failed');
          }
        } else {
          toast.error('Apple Sign-In cancelled');
        }
      } catch (error) {
        console.error('[AUTH][APPLE] outer error:', error);
        const msg = (error && (error.message || error.toString())) || '';
        if (msg.includes('canceled') || msg.includes('cancelled') || msg.includes('1001')) {
          // user cancelled
        } else {
          toast.error('Apple Sign-In failed. Please try again.');
        }
      } finally {
        setAppleSignInLoading(false);
      }
    } else {
      toast.error('Apple Sign-In is only available on iOS devices');
    }
  };

  // Magic Link email authentication
  const handleEmailLogin = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingMagicLink(true);
    try {
      // On native, use the production backend URL for the callback.
      // On web, use current origin to match the active environment.
      const isNative = Capacitor.isNativePlatform();
      const baseUrl = backendOrigin();
      const redirectUrl = baseUrl + '/auth-callback';
      console.log('[AUTH][MAGIC] sending magic link', { email, isNative, baseUrl, platform: isNative ? Capacitor.getPlatform() : 'web' });
      const res = await authAPI.sendMagicLink(email, redirectUrl);
      console.log('[AUTH][MAGIC] backend response:', res?.status, res?.data);
      setMagicLinkSent(true);
      toast.success('Magic link sent! Check your email.');
    } catch (error) {
      // Log the concrete failure shape so you can see it in Xcode console.
      console.error('[AUTH][MAGIC] send failed', {
        message: error?.message,
        code: error?.code,
        name: error?.name,
        respStatus: error?.response?.status,
        respData: error?.response?.data,
        requestURL: error?.config?.baseURL + (error?.config?.url || ''),
      });
      const status = error?.response?.status;
      if (!error || !error.response) {
        toast.error('Network issue — check your connection and try again.');
      } else if (status === 429) {
        toast.error('Too many attempts. Please wait a moment and try again.');
      } else if (status === 400 || status === 422) {
        toast.error('Please enter a valid email address.');
      } else {
        toast.error('Failed to send magic link. Please try again.');
      }
    } finally {
      setSendingMagicLink(false);
    }
  };

  // Already-hydrated session: route during render — no session-check
  // spinner and no login-screen flash.
  if (user && !switchRequested) {
    return <Navigate to={user.onboarding_complete ? '/home' : '/onboarding/setup'} replace />;
  }

  // While we check for an existing session, keep the screen blank/loading.
  // On iOS the native splash covers us until React hits this — users never
  // see a flash of the login buttons if they're already authenticated.
  if (checkingSession) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center p-6"
        data-testid="login-session-check"
      >
        <div className="w-10 h-10 border-[3px] border-[#14D5F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If magic link was sent, show confirmation screen
  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="text-center space-y-6 max-w-sm w-full"
        >
          <div className="w-16 h-16 rounded-full bg-[#14D5F6]/10 flex items-center justify-center mx-auto">
            <Mail size={32} className="text-[#14D5F6]" />
          </div>
          
          <div className="space-y-2">
            <h1 className="font-heading font-bold text-2xl text-gray-900">Check your email</h1>
            <p className="text-gray-500 text-sm">
              We sent a magic link to <span className="font-medium text-gray-700">{email}</span>
            </p>
          </div>
          
          <p className="text-gray-400 text-xs">
            Click the link in your email to sign in. The link expires in 15 minutes.
          </p>
          
          <Button
            onClick={() => {
              setMagicLinkSent(false);
              setEmail('');
              setShowEmailInput(false);
            }}
            variant="ghost"
            className="text-[#14D5F6] hover:text-[#14D5F6]/80"
          >
            Use a different email
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center space-y-10 max-w-sm w-full"
      >
        {/* Logo/Header */}
        <div className="space-y-3">
          <h1 
            className="font-heading font-bold text-5xl tracking-tight text-gray-900"
            style={{ letterSpacing: '-0.02em' }}
          >
            FITXESS
          </h1>
          <p className="text-gray-500 text-base">
            Plan smarter. Track easier. Make progress that fits your life.
          </p>
        </div>

        {/* Session-expired banner. Surfaces ONLY when the axios 401
            handler just cleared this device's session (read once from
            sessionStorage, cleared on read so a manual /login visit
            doesn't show the banner). Replaces the earlier URL-param
            approach that didn't survive the no-window.location-replace
            fix for iOS WebView. */}
        {sessionExpiredBanner && (
          <div
            data-testid="login-session-expired-banner"
            className="px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-left text-sm text-amber-900"
          >
            Your session expired. Please sign in again to continue.
          </div>
        )}

        {/* Auth Buttons */}
        <div className="space-y-3">
          {/* Continue with Google */}
          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="w-full h-14 rounded-full border border-gray-300 bg-transparent text-gray-900 font-medium hover:border-[#14D5F6] hover:text-[#14D5F6] transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Continue with Apple - Only show on iOS or web */}
          <button
            onClick={handleAppleLogin}
            disabled={appleSignInLoading}
            data-testid="apple-login-btn"
            className="w-full h-14 rounded-full border border-gray-300 bg-transparent text-gray-900 font-medium hover:border-[#14D5F6] hover:text-[#14D5F6] transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {appleSignInLoading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                Continue with Apple
              </>
            )}
          </button>

          {/* Continue with Email */}
          {!showEmailInput ? (
            <button
              onClick={() => setShowEmailInput(true)}
              data-testid="email-login-btn"
              className="w-full h-14 rounded-full border border-gray-300 bg-transparent text-gray-700 font-medium hover:border-[#14D5F6] hover:text-[#14D5F6] transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-3"
            >
              <Mail size={20} />
              Continue with Email
            </button>
          ) : (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
                className="h-14 rounded-full px-6 border-2 border-gray-200 focus:border-[#14D5F6] focus:ring-[#14D5F6]/20 text-center"
                autoFocus
                data-testid="email-input"
              />
              <Button
                onClick={handleEmailLogin}
                disabled={sendingMagicLink || !email}
                data-testid="send-magic-link-btn"
                className="w-full h-14 rounded-full bg-[#14D5F6] text-white font-medium hover:bg-[#14D5F6]/90 transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {sendingMagicLink ? (
                  <>
                    <Loader2 size={20} className="animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  'Send Link'
                )}
              </Button>
              <button
                onClick={() => {
                  setShowEmailInput(false);
                  setEmail('');
                }}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          )}
        </div>

        {/* Terms */}
        <p className="text-xs text-gray-400 leading-relaxed">
          By continuing, you agree to our{' '}
          <a
            href={TERMS_URL}
            onClick={(e) => { e.preventDefault(); openInAppBrowser(TERMS_URL); }}
            className="text-[#14D5F6] hover:underline cursor-pointer"
            data-testid="login-terms-link"
          >
            Terms of Use
          </a>
          {' '}and{' '}
          <a
            href={PRIVACY_URL}
            onClick={(e) => { e.preventDefault(); openInAppBrowser(PRIVACY_URL); }}
            className="text-[#14D5F6] hover:underline cursor-pointer"
            data-testid="login-privacy-link"
          >
            Privacy Policy
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
